import type { CSSProperties } from 'react';
import { type ComponentPropsWithoutRef, type RefObject, useRef } from 'react';
import styled, { css } from 'styled-components';

import { hiddenScrollbar } from '@/styles/style';
import keymap from '@/utils/keymap';
import { stopEvent } from '@/utils/util';

import useFlexibleTextarea from './useFlexibleTextarea';

type Props = ComponentPropsWithoutRef<'textarea'> & {
  initFocus?: boolean;
  hero?: boolean;
  style?: CSSProperties;
  minHeight?: number;
  showBorder?: boolean;
  css?: StyledCss;
};

const FlexibleTextarea = (props: Props) => {
  const ref = useRef(null) as RefObject<HTMLTextAreaElement>;

  useFlexibleTextarea(
    {
      initFocus: props.initFocus,
      value: props.value as string,
      minHeight: props.minHeight,
    },
    ref,
  );

  return (
    <Style
      {...props}
      ref={ref}
      onKeyUp={stopEvent}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.nativeEvent.isComposing) return;
        if (keymap.isEscape(e)) {
          ref.current?.blur();
        }
        props.onKeyDown?.(e);
      }}
    />
  );
};

export const Style = styled.textarea<Props>`
  height: 38px;
  padding: 12px;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.theme.colors.gray800};
  outline: none;
  border: 1px solid ${({ theme }) => theme.colors.gray400};
  border-radius: 4px;
  transition: all 0.2s;
  resize: none;
  background-color: ${(props) => props.theme.colors.gray100};

  ${hiddenScrollbar};

  :hover {
    filter: brightness(0.98);
  }

  :focus {
    filter: brightness(0.96);
    :hover {
      filter: brightness(0.94);
    }
  }

  &::placeholder {
    color: ${(props) => props.theme.colors.gray500};
  }

  &:disabled {
    background-color: ${(props) => props.theme.colors.gray300};
    border: none;
    color: ${(props) => props.theme.colors.gray500};
  }

  ${({ showBorder }) =>
    showBorder &&
    css`
      border: 1px solid ${(props) => props.theme.colors.gray400};
    `}

  ${({ hero }) =>
    hero &&
    css`
      width: 100%;
      height: 44px;
      padding: 11px;
      font-size: 16px;
      font-weight: 600;
    `};

  ${(props) => props.css && props.css};
`;

export default FlexibleTextarea;
